import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './SingleChooser.module.scss';
import classNames from 'classnames';
import Icon from '../Icon/Icon';
import Cookies from 'js-cookie';
import { ucFirst } from '../../utils/caseconverters';

const SingleChooser = ({
    placeholder,
    tooltip,
    options = [],
    secondaryOptionName = 'Other',
    secondaryOptions = null,
    onChange = () => {},
    activeValue,
    showClearButton = false,
    alignRight = false,
    alignMenuRight = false,
    tiny = false,
    border = false,
    modifiers = [],
    onClick = () => {},
}) => {
    const [hideTooltip, setHideTooltip] = useState(
        typeof Cookies.get('productTooltip') !== 'undefined'
    );
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [secondaryOptionsExpanded, setSecondaryOptionsExpanded] =
        useState(false);
    const [mounted, setMounted] = useState(false);
    const containerRef = useRef(null);

    const handleOutsideClick = (event) => {
        if (
            containerRef.current &&
            !containerRef.current.contains(event.target)
        ) {
            setIsMenuOpen(false);
        }
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleSecondaryMenu = () => {
        setSecondaryOptionsExpanded(!secondaryOptionsExpanded);
    };

    const handleOptionOnClick = (option) => {
        onChange(option.value);
        toggleMenu();
    };

    const handleClearOptionOnClick = () => {
        onChange(undefined);
    };

    const handleClear = () => {
        onClick();
        setIsMenuOpen(false);
    }

    const closeTooltip = () => {
        Cookies.set('productTooltip', 'hide', { expires: 7 });
        setHideTooltip(true);
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [containerRef]);

    // const visibleOptions = secondaryOptionsExpanded
    //     ? [...options, ...secondaryOptions]
    //     : options;

    useEffect(() => {
        setMounted(true);
    }, []);

    const classes = classNames(
        styles['SingleChooser'],
        {
            [styles['SingleChooser--Active']]: isMenuOpen,
            [styles['SingleChooser--AlignRight']]: alignRight,
            [styles['SingleChooser--AlignMenuRight']]: alignMenuRight,
            [styles['SingleChooser--Tiny']]: tiny,
            [styles['SingleChooser--Border']]: border,
            [styles['SingleChooser--SecondaryActive']]:
                secondaryOptionsExpanded,
        },
        modifiers.map(
            (modifier) => styles['SingleChooser--' + ucFirst(modifier)] + ' '
        )
    );

    return (
        <React.Fragment>
            {mounted && (
                <div className={classes} ref={containerRef}>
                    <div className={styles['SingleChooser__Container']}>
                        <button
                            className={styles['SingleChooser__Toggler']}
                            onClick={toggleMenu}>
                            <div
                                className={
                                    styles['SingleChooser__SelectedValue']
                                }>
                                {placeholder}
                            </div>
                            <span className={styles['SingleChooser__Icon']}>
                                <Icon type={'chevron'} />
                            </span>
                        </button>
                        {isMenuOpen && (
                            <div className={styles['SingleChooser__Menu']}>
                                <ul
                                    className={
                                        styles['SingleChooser__Options']
                                    }>
                                    {showClearButton && (
                                        <li 
                                        className={
                                            styles['SingleChooser__Option']
                                        }>
                                            <button
                                                onClick={handleClear}
                                                className={classNames(
                                                    styles[
                                                        'SingleChooser__OptionButton'
                                                    ],
                                                    styles[
                                                        'SingleChooser__OptionButton--Clear'
                                                    ]
                                                )}>
                                                Clear selection
                                            </button>
                                        </li>
                                    )}
                                    {options.map((option, index) => (
                                        <li
                                            className={
                                                styles['SingleChooser__Option']
                                            }
                                            key={index}>
                                            <button
                                                onClick={() => {
                                                    handleOptionOnClick(option);
                                                }}
                                                className={classNames(
                                                    styles[
                                                        'SingleChooser__OptionButton'
                                                    ],
                                                    {
                                                        [styles[
                                                            'SingleChooser__OptionButton--Active'
                                                        ]]:
                                                            option ===
                                                            activeValue,
                                                    }
                                                )}>
                                                {option.label}
                                            </button>
                                        </li>
                                    ))}
                                    {secondaryOptions &&
                                        secondaryOptions.length > 0 && (
                                            <li
                                                className={
                                                    styles[
                                                        'SingleChooser__Option'
                                                    ]
                                                }>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleSecondaryMenu();
                                                    }}
                                                    className={classNames(
                                                        styles[
                                                            'SingleChooser__OptionButton'
                                                        ],
                                                        styles[
                                                            'SingleChooser__OptionButton--Secondary'
                                                        ]
                                                    )}>
                                                    {secondaryOptionName}
                                                    <span
                                                        className={
                                                            styles[
                                                                'SingleChooser__SecondaryIcon'
                                                            ]
                                                        }>
                                                        <Icon
                                                            type={'chevron'}
                                                        />
                                                    </span>
                                                </button>
                                            </li>
                                        )}
                                    {secondaryOptions && secondaryOptionsExpanded &&
                                        secondaryOptions.map(
                                            (option, index) => (
                                                <li
                                                    className={classNames(
                                                        styles[
                                                            'SingleChooser__Option'
                                                        ],
                                                        styles[
                                                            'SingleChooser__Option--Secondary'
                                                        ]
                                                    )}
                                                    key={index}>
                                                    <button
                                                        onClick={() => {
                                                            handleOptionOnClick(
                                                                option
                                                            );
                                                        }}
                                                        className={classNames(
                                                            styles[
                                                                'SingleChooser__OptionButton'
                                                            ],
                                                            {
                                                                [styles[
                                                                    'SingleChooser__OptionButton--Active'
                                                                ]]:
                                                                    option ===
                                                                    activeValue,
                                                            }
                                                        )}>
                                                        {option.label}
                                                    </button>
                                                </li>
                                            )
                                        )}
                                </ul>
                            </div>
                        )}
                    </div>
                    {typeof window !== 'undefined' &&
                        !hideTooltip &&
                        tooltip && (
                            <div
                                className={classNames(
                                    styles['SingleChooser__Tooltip'],
                                    {
                                        [styles[
                                            'SingleChooser__Tooltip--Hidden'
                                        ]]: activeValue,
                                    }
                                )}>
                                <span>{tooltip}</span>
                                <button
                                    className={
                                        styles['SingleChooser__TooltipButton']
                                    }
                                    onClick={() => {
                                        closeTooltip();
                                    }}>
                                    <Icon type="crossSmall" />
                                    <span className="sr-only">Close</span>
                                </button>
                            </div>
                        )}
                </div>
            )}
        </React.Fragment>
    );
};

SingleChooser.propTypes = {
    placeHolder: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            children: PropTypes.arrayOf(PropTypes.node),
        })
    ),
};

export default SingleChooser;
